import React, { useState, useEffect } from "react";
import {
  X,
  Copy,
  Check,
  Phone,
  Mail,
  CreditCard,
  Smartphone,
  QrCode,
} from "lucide-react";
import { getData, postData } from "../api/apiService";
import { toast } from "react-toastify";
import { PRICING } from "../api/endpointMap";

const PaymentDrawer = ({
  isOpen,
  setIsOpen,
  selectedPortals = [],
  promoCode = "",
  onDetailsSubmission,
}) => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedUPI, setCopiedUPI] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState({
    transactionId: "",
    name: "",
    phoneNumber: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pinCode: "",
    gstNumber: "",
    payment_method: "UPI",
  });

  const fetchPaymentDetails = async () => {
    setLoading(true);
    try {
      const response = await getData(
        PRICING.GET_PAYMENT_DETAILS,
        {}
      );

      setPaymentDetails(response.result);
    } catch (error) {
      console.error("Error fetching payment details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUPI = async () => {
    if (paymentDetails?.upi_id) {
      try {
        await navigator.clipboard.writeText(paymentDetails.upi_id);
        setCopiedUPI(true);
        setTimeout(() => setCopiedUPI(false), 2000);
      } catch (err) {
        console.error("Failed to copy UPI ID:", err);
      }
    }
  };

  const handlePaymentDone = () => {
    setShowConfirmation(true);
  };

  useEffect(() => {
    fetchPaymentDetails();
  }, []);

  const handleConfirmationSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        selected_portals: selectedPortals,
        promo_code: promoCode,
        name: confirmationDetails.name,
        phone_number: confirmationDetails.phoneNumber,
        email: confirmationDetails.email,
        address: confirmationDetails.address,
        city: confirmationDetails.city,
        state: confirmationDetails.state,
        pincode: confirmationDetails.pinCode,
        gst_number: confirmationDetails.gstNumber,
        transaction_id: confirmationDetails.transactionId,
        amount: confirmationDetails.amount,
        payment_method: confirmationDetails.payment_method,
      };

      const response = await postData(
        PRICING.SUBMIT_BILLING,
        payload
      );

      toast.success(
        "Payment confirmation submitted successfully! We will verify and activate your account soon."
      );

      if (onDetailsSubmission) {
        onDetailsSubmission();
      }

      setShowConfirmation(false);
      setIsOpen(false);

      setConfirmationDetails({
        transactionId: "",
        name: "",
        phoneNumber: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pinCode: "",
        gstNumber: "",
        amount: "",
        payment_method: "upi",
      });
    } catch (error) {
      console.error("Error submitting confirmation:", error);
      toast.error(
        typeof error === "string" ? error : "Failed to submit payment confirmation. Please try again or contact support."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfirmationDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isOpen) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handleKeyDown = (e) => {
      if (isOpen && (e.key === "F5" || (e.ctrlKey && e.key === "r"))) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Drawer Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsOpen(false)}
          />

          {/* Drawer */}
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">
                  Complete Payment
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">
                      Loading payment details...
                    </p>
                  </div>
                ) : paymentDetails ? (
                  <div className="space-y-6">
                    {/* Payment Methods */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                        <CreditCard className="w-5 h-5 mr-2" />
                        Payment Methods
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <QrCode className="w-8 h-8 text-blue-600 mb-2" />
                          <p className="text-sm font-medium text-gray-900">
                            QR Code
                          </p>
                          <p className="text-xs text-gray-600">Scan to pay</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <Smartphone className="w-8 h-8 text-blue-600 mb-2" />
                          <p className="text-sm font-medium text-gray-900">
                            UPI
                          </p>
                          <p className="text-xs text-gray-600">
                            Direct transfer
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* QR Code Section */}
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Scan QR Code
                      </h3>
                      <div className="bg-white rounded-lg p-6 shadow-sm border inline-block">
                        <img
                          src={paymentDetails.qr_code}
                          alt="Payment QR Code"
                          className="w-40 h-40 mx-auto"
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-3">
                        Open any UPI app and scan this QR code to pay
                      </p>
                    </div>

                    {/* UPI ID Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Or Pay via UPI ID
                      </h3>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-white rounded-lg p-3 border">
                          <p className="text-sm text-gray-600">UPI ID</p>
                          <p className="font-mono text-lg text-gray-900">
                            {paymentDetails.upi_id}
                          </p>
                        </div>
                        <button
                          onClick={handleCopyUPI}
                          className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          {copiedUPI ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      {copiedUPI && (
                        <p className="text-sm text-green-600 mt-2">
                          UPI ID copied to clipboard!
                        </p>
                      )}
                    </div>

                    {paymentDetails.payment_methods.includes("Net Banking") && (
                      <div className="bg-gray-100 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          Bank Transfer Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">
                              Bank Name:
                            </span>{" "}
                            {paymentDetails.bank_name}
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">
                              Account No:
                            </span>{" "}
                            {paymentDetails.account_no}
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">
                              IFSC Code:
                            </span>{" "}
                            {paymentDetails.ifsc_code}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          You can use these details for NEFT/RTGS/IMPS
                          transfers.
                        </p>
                      </div>
                    )}

                    {/* Payment Instructions */}
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <h4 className="font-semibold text-amber-900 mb-2">
                        Payment Instructions
                      </h4>
                      <ul className="text-sm text-amber-800 space-y-1">
                        <li>• Complete the payment using any UPI app</li>
                        <li>• Take a screenshot of the payment confirmation</li>
                        <li>• Click "Payment Done" button below</li>
                        <li>• We'll verify and activate your account</li>
                      </ul>
                    </div>

                    {/* Payment Done Button */}
                    <button
                      onClick={handlePaymentDone}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-colors duration-200"
                    >
                      Payment Done
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-600">
                      Failed to load payment details. Please try again.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-60 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-[90vw] max-h-[90vh] overflow-y-hidden">
              <form onSubmit={handleConfirmationSubmit}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      Billing Details
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowConfirmation(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                      disabled={submitting}
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>After payment completion:</strong>
                      <br />
                      Call <Phone className="w-4 h-4 inline mx-1" />
                      <span className="font-semibold">7015591070</span> or email
                      <Mail className="w-4 h-4 inline mx-1" />
                      <span className="font-semibold">info@infinabs.ai</span> to
                      activate your account
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-4 overflow-y-auto h-[300px]">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Transaction ID{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="transactionId"
                            value={confirmationDetails.transactionId}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter transaction ID"
                            required
                            disabled={submitting}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={confirmationDetails.name}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter your full name"
                            required
                            disabled={submitting}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phoneNumber"
                            value={confirmationDetails.phoneNumber}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter phone number"
                            required
                            disabled={submitting}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={confirmationDetails.email}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter email address"
                            required
                            disabled={submitting}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="address"
                            value={confirmationDetails.address}
                            onChange={handleInputChange}
                            rows="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="Enter your address"
                            required
                            disabled={submitting}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={confirmationDetails.city}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter city"
                            required
                            disabled={submitting}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="state"
                            value={confirmationDetails.state}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter state"
                            required
                            disabled={submitting}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pin Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="pinCode"
                            value={confirmationDetails.pinCode}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter pin code"
                            maxLength="6"
                            pattern="[0-9]{6}"
                            required
                            disabled={submitting}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            GST Number{" "}
                            <span className="text-gray-400">(Optional)</span>
                          </label>
                          <input
                            type="text"
                            name="gstNumber"
                            value={confirmationDetails.gstNumber}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter GST number"
                            maxLength="15"
                            disabled={submitting}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="amount"
                            inputMode="numeric"
                            value={confirmationDetails.amount}
                            onChange={(e) => {
                              const cleaned = e.target.value.replace(/\D/g, "");
                              setConfirmationDetails((prev) => ({
                                ...prev,
                                amount: cleaned.replace(/^0+/, "") || "0",
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter amount"
                            required
                            disabled={submitting}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Payment Mode <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="payment_method"
                            value={confirmationDetails.payment_method}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            disabled={submitting}
                          >
                            <option value="UPI">UPI</option>
                            <option value="Net Banking">Net Banking</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 p-4 flex justify-between bg-white sticky bottom-0 z-10">
                      <button
                        type="button"
                        onClick={() => setShowConfirmation(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors mr-2"
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Submitting...
                          </div>
                        ) : (
                          "Submit Confirmation"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentDrawer;
